import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import type { Role } from '@shared/collaboration/access'

export type ResourceType = 'project' | 'model'

/** Backend enriches each raw grant row with the resolved principal (name/email or team name). */
export interface GrantPrincipal {
  type: 'user' | 'team'
  id: string
  name: string
  email?: string
}

/** Raw grant row as persisted — no enrichment. What the backend POST handler returns. */
export interface RawGrant {
  id: string
  tenantId: string
  scopeType: ResourceType
  scopeId: string
  principalType: 'user' | 'team'
  principalId: string
  role: Role
  grantedBy: string
  createdAt?: string
}

/** GET list is enriched with the resolved principal; POST create is not (see RawGrant). */
export interface Grant extends RawGrant {
  principal: GrantPrincipal
}

export interface CreateGrantParams {
  principalType: 'user' | 'team'
  principalId: string
  role: Role
}

function grantsPath(resourceType: ResourceType, resourceId: string): string {
  return `/${resourceType}s/${resourceId}/grants`
}

function grantsKey(resourceType: ResourceType, resourceId: string | undefined): Array<string | undefined> {
  return ['grants', resourceType, resourceId]
}

/** People/teams with access to a project or model, enriched with principal name/email. */
export function useGrants(resourceType: ResourceType, resourceId: string | undefined): UseQueryResult<Grant[]> {
  const api = useApi()

  return useQuery<Grant[]>({
    queryKey: grantsKey(resourceType, resourceId),
    queryFn: (): Promise<Grant[]> => api.get<Grant[]>(grantsPath(resourceType, resourceId as string)),
    enabled: !!resourceId,
  })
}

export function useCreateGrant(
  resourceType: ResourceType,
  resourceId: string,
): UseMutationResult<RawGrant, Error, CreateGrantParams> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<RawGrant, Error, CreateGrantParams>({
    mutationFn: (params): Promise<RawGrant> => api.post<RawGrant>(grantsPath(resourceType, resourceId), params),
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: grantsKey(resourceType, resourceId) })
    },
  })
}

export function useDeleteGrant(
  resourceType: ResourceType,
  resourceId: string,
): UseMutationResult<void, Error, string> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (grantId): Promise<void> => api.delete(`${grantsPath(resourceType, resourceId)}/${grantId}`),
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: grantsKey(resourceType, resourceId) })
    },
  })
}
